import { FC, ReactNode } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Typography
} from "@mui/material";
import { Broker } from "../../../../api/Api";
import { Colors } from "../../../../theme";
import { getStatus } from "../../../../components/lifecycle/departmentProgress/utils";
import { MergerChip } from "../../../../components/mergerChip";
import { mergerSummaryRows } from "../../../mergers/mergerSummaryRows";

type Props = {
  broker: Broker;
  /** Rendered after the lifecycle sections, in the same grid (operational readiness, ADHO-551). */
  extraSections?: ReactNode;
};

const fmt = (value: unknown): ReactNode => {
  if (value === null || value === undefined || value === "") return "N/A";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const Field: FC<{ label: string; value: ReactNode }> = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: { xs: "column", sm: "row" },
      gap: { xs: 0.25, sm: 2 },
      paddingY: 0.5
    }}
  >
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ flex: { sm: "0 0 180px" } }}
    >
      {label}
    </Typography>
    <Typography variant="body2" sx={{ flex: 1 }}>
      {value}
    </Typography>
  </Box>
);

const Section: FC<{
  title: string;
  headerAction?: ReactNode;
  showDivider?: boolean;
  children: ReactNode;
}> = ({ title, headerAction, showDivider = true, children }) => (
  <Card
    variant="outlined"
    elevation={3}
    sx={{
      breakInside: "avoid",
      marginBottom: 2
    }}
  >
    <CardContent
      sx={{
        padding: 2,
        "&:last-child": {
          paddingBottom: 2
        }
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          marginBottom: showDivider ? 1 : 0
        }}
      >
        <Typography variant="h6">{title}</Typography>
        {headerAction}
      </Box>
      {showDivider && <Divider sx={{ marginBottom: 1 }} />}
      {children}
    </CardContent>
  </Card>
);

const SignOffNote: FC<{ note?: string | null }> = ({ note }) => {
  if (!note) return null;
  return <Field label="Sign-off note" value={note} />;
};

const LifecycleStatus: FC<{
  progress?: string | null;
  hasSignOffNote?: boolean;
}> = ({ progress, hasSignOffNote = false }) => {
  const completed = getStatus(progress) === "completed";
  const isGreen = completed && !hasSignOffNote;
  const label = completed
    ? hasSignOffNote
      ? "Completed with remarks"
      : "Completed"
    : "Not completed";

  return (
    <Chip
      size="small"
      label={label}
      sx={{
        backgroundColor: isGreen ? Colors.NICE_GREEN : Colors.MFT_ORANGE,
        color: "#FFF",
        flexShrink: 0,
        marginTop: "2px"
      }}
    />
  );
};

/**
 * The read-only left column of a broker sign-off: root fields, then each lifecycle as a status
 * chip plus its sign-off note. Same shape as the counterparty and country panels (ADHO-443), with
 * five lifecycles rather than seven: brokers have no treasury and no tech.
 *
 * The per-field breakdown deliberately lives only in the PDF, so a VP reading this sees what is
 * un-done rather than a wall of values.
 */
export const SignOffDetailsPanel: FC<Props> = ({ broker, extraSections }) => {
  const marketAccess = broker.marketAccessLifecycle;
  const compliance = broker.complianceLifecycle;
  const legal = broker.legalLifecycle;
  const risk = broker.riskLifecycle;
  const creditRisk = broker.creditRiskLifecycle;
  const finance = broker.financeLifecycle;

  return (
    <Box padding={1}>
      <Card elevation={3}>
        <CardContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ marginBottom: 1 }}>
                Basic Information
              </Typography>
              <Divider sx={{ marginBottom: 1 }} />
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                flex: 1,
                gap: 3
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Field label="Trading team" value={fmt(broker.tradingTeam)} />
                <Field
                  label="Contracting Entity"
                  value={fmt(broker.contractingEntity)}
                />
                <Field
                  label="Requester"
                  value={fmt(broker.contactPersonTradingTeam)}
                />
                <Field
                  label="Broker contact"
                  value={fmt(broker.brokerContactName)}
                />
                <Field
                  label="Broker contact email"
                  value={fmt(broker.brokerContactEmail)}
                />
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Field label="Product Type" value={fmt(broker.productType)} />
                <Field label="Venue" value={fmt(broker.venue)} />
                <Field
                  label="Settlement type"
                  value={fmt(broker.tradingSettlementType)}
                />
                <Field
                  label="Agreement type"
                  value={fmt(broker.agreementType)}
                />
                <Field
                  label="Transaction type"
                  value={fmt(broker.transactionType)}
                />
                <Field
                  label="Expected deal volume/size"
                  value={
                    broker.dealVolume
                      ? `${broker.dealVolume} ${broker.unit || ""}`
                      : "N/A"
                  }
                />
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2
              }}
            >
              <SignOffNote note={marketAccess?.signOffNote} />
            </Box>
          </Box>
          <Box
            sx={{
              marginTop: 2,
              columnCount: { xs: 1, lg: 2 },
              columnGap: 4
            }}
          >
            {broker.isMerger && (
              // Stands in for the Market Access lifecycle a merger never ran. No documents
              // here: VPs are not shown attachments (frontend decision, see CLAUDE.md).
              <Section title="Merger" headerAction={<MergerChip />}>
                {mergerSummaryRows(
                  "broker",
                  broker.mergerDescription,
                  broker.marketAccessLifecycle
                ).map(([label, value]) => (
                  <Field key={label} label={label} value={value} />
                ))}
              </Section>
            )}
            <Section
              title="Compliance"
              headerAction={
                <LifecycleStatus
                  progress={broker.progress?.compliance}
                  hasSignOffNote={Boolean(compliance?.signOffNote)}
                />
              }
              showDivider={Boolean(compliance?.signOffNote)}
            >
              <SignOffNote note={compliance?.signOffNote} />
            </Section>

            <Section
              title="Legal"
              headerAction={
                <LifecycleStatus
                  progress={broker.progress?.legal}
                  hasSignOffNote={Boolean(legal?.signOffNote)}
                />
              }
              showDivider={Boolean(legal?.signOffNote)}
            >
              <SignOffNote note={legal?.signOffNote} />
            </Section>

            <Section
              title="Risk"
              headerAction={
                <LifecycleStatus
                  progress={broker.progress?.risk}
                  hasSignOffNote={Boolean(risk?.signOffNote)}
                />
              }
              showDivider={Boolean(risk?.signOffNote)}
            >
              <SignOffNote note={risk?.signOffNote} />
            </Section>

            <Section
              title="Credit Risk"
              headerAction={
                <LifecycleStatus
                  progress={broker.progress?.creditRisk}
                  hasSignOffNote={Boolean(creditRisk?.signOffNote)}
                />
              }
              showDivider={Boolean(creditRisk?.signOffNote)}
            >
              <SignOffNote note={creditRisk?.signOffNote} />
            </Section>

            <Section
              title="Finance"
              headerAction={
                <LifecycleStatus
                  progress={broker.progress?.finance}
                  hasSignOffNote={Boolean(finance?.signOffNote)}
                />
              }
              showDivider={Boolean(finance?.signOffNote)}
            >
              <SignOffNote note={finance?.signOffNote} />
            </Section>
            {extraSections}
          </Box>

          <Box sx={{ marginTop: 2 }}>
            {broker.signOff?.summary && (
              <Section title="Summary">
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {broker.signOff.summary}
                </Typography>
              </Section>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
